import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { BAND_META } from './healthScore';
import { ENGINEER_STATE_META, type PerfNode } from './performanceModel';
import type { ScopeWindow } from './scopeWindow';
import { windowLabel } from './scopeWindow';
import { formatNumber } from './formatNumber';

/* ============================================================
 * WYSIWYG report export. The payload is exactly the rows the
 * Explorer is showing — same scope, same filters, same order.
 * Formats: CSV / XLSX (flat table + scope metadata columns) and
 * a branded Sovereign-Emerald PDF for management.
 * ============================================================ */

export type ExportFormat = 'csv' | 'xlsx' | 'pdf';

export interface ReportPayload {
  /** Breadcrumb path, e.g. ['India', 'Tamil Nadu', 'Jayanthi 2.6 MWp']. */
  scopePath: string[];
  levelLabel: string;
  window: ScopeWindow;
  deltaPhrase: string;
  rows: PerfNode[];
  topPerformers: PerfNode[];
  needsAttention: PerfNode[];
}

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'all';

export function reportFilename(payload: ReportPayload, ext: ExportFormat): string {
  const scopeSlug = payload.scopePath.length > 1
    ? payload.scopePath.slice(1).map(slug).join('-')
    : 'all';
  return `sorigin-performance_${scopeSlug}_${payload.window.from}_${payload.window.to}.${ext}`;
}

const scoreOf = (n: PerfNode) => (n.score ? String(n.score.score) : '—');
const bandOfNode = (n: PerfNode) => (n.score ? BAND_META[n.score.band].label : 'No scheduled work');
const deltaOf = (n: PerfNode) => (n.delta === null ? '—' : `${n.delta > 0 ? '+' : ''}${n.delta}`);
const completionOf = (n: PerfNode) =>
  n.scheduled > 0 ? `${Math.round((n.completed / n.scheduled) * 100)}%` : '—';
const passRateOf = (n: PerfNode) =>
  n.completed > 0 ? `${Math.round((n.counts.pass / n.completed) * 100)}%` : '—';

function flatRows(payload: ReportPayload): Array<Record<string, string | number>> {
  const generatedAt = dayjs().format('YYYY-MM-DD HH:mm');
  return payload.rows.map((row, i) => {
    const record: Record<string, string | number> = {
      Rank: i + 1,
      Name: row.name,
      'Health score': scoreOf(row),
      Band: bandOfNode(row),
      [`Δ ${payload.deltaPhrase}`]: deltaOf(row),
      Completed: row.completed,
      Scheduled: row.scheduled,
      'Completion %': completionOf(row),
      'Pass % (of completed)': passRateOf(row),
      Overdue: row.overdue,
      Open: row.open,
    };
    if (row.kind === 'state') {
      record['Locations'] = row.locationCount;
      record['Projects'] = row.projectCount;
      record['Engineers'] = row.engineerCount;
    } else if (row.kind === 'location') {
      record['State'] = row.stateName;
      record['Projects'] = row.projectCount;
      record['Engineers'] = row.engineerCount;
    } else if (row.kind === 'project') {
      record['Location'] = row.locationName;
      record['State'] = row.stateName;
      record['Engineers'] = row.engineerCount;
    } else {
      record['Status'] = ENGINEER_STATE_META[row.state].label;
      record['Projects'] = row.projectCount;
      record['Last submission'] =
        row.daysSinceLastSubmit === null ? 'Never' : `${row.daysSinceLastSubmit}d ago`;
    }
    record['Scope'] = payload.scopePath.join(' → ');
    record['From'] = payload.window.from;
    record['To'] = payload.window.to;
    record['Generated at'] = generatedAt;
    return record;
  });
}

function exportCsv(payload: ReportPayload): void {
  const rows = flatRows(payload);
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0] ?? {});
  const escape = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => escape(r[h] ?? '')).join(',')),
  ].join('\n');
  saveAs(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }), reportFilename(payload, 'csv'));
}

function exportXlsx(payload: ReportPayload): void {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(flatRows(payload)), payload.levelLabel.slice(0, 31));
  XLSX.writeFile(wb, reportFilename(payload, 'xlsx'));
}

/* Sovereign Emerald print tokens (hex → RGB triplets for jsPDF). */
const PDF = {
  paper: [242, 234, 219] as const,
  plate: [250, 247, 238] as const,
  racing: [11, 59, 46] as const,
  bottle: [31, 90, 71] as const,
  champagne: [201, 169, 97] as const,
  champagneDeep: [138, 90, 8] as const,
  ink: [42, 31, 23] as const,
  caption: [124, 138, 130] as const,
  oxblood: [159, 18, 57] as const,
};

function exportPdf(payload: ReportPayload): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;

  // Masthead — cream band, racing-green wordmark, champagne rule.
  doc.setFillColor(...PDF.paper);
  doc.rect(0, 0, pageWidth, 92, 'F');
  doc.setTextColor(...PDF.racing);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(19);
  doc.text('SORIGIN', margin, 38);
  doc.setFontSize(10);
  doc.setTextColor(...PDF.champagneDeep);
  doc.text('OPERATIONS & MAINTENANCE · PERFORMANCE REPORT', margin, 54);
  doc.setDrawColor(...PDF.champagne);
  doc.setLineWidth(1.2);
  doc.line(margin, 64, pageWidth - margin, 64);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...PDF.ink);
  doc.text(
    `${payload.scopePath.join('  →  ')}   ·   ${windowLabel(payload.window)}   ·   generated ${dayjs().format('D MMM YYYY, HH:mm')}`,
    margin,
    80
  );

  // Summary line with honest denominators.
  const totals = payload.rows.reduce(
    (acc, r) => {
      acc.completed += r.completed;
      acc.scheduled += r.scheduled;
      acc.overdue += r.overdue;
      acc.open += r.open;
      return acc;
    },
    { completed: 0, scheduled: 0, overdue: 0, open: 0 }
  );
  const completionPct = totals.scheduled > 0 ? Math.round((totals.completed / totals.scheduled) * 100) : 0;
  doc.setFontSize(10.5);
  doc.setTextColor(...PDF.bottle);
  doc.setFont('helvetica', 'bold');
  doc.text(
    `${payload.levelLabel} · ${payload.rows.length} entries · ${formatNumber(totals.completed)} completed of ${formatNumber(totals.scheduled)} scheduled (${completionPct}%) · ${formatNumber(totals.overdue)} overdue of ${formatNumber(totals.open)} open`,
    margin,
    114
  );

  const body = payload.rows.map((row, i) => [
    String(i + 1),
    row.name,
    scoreOf(row),
    bandOfNode(row),
    deltaOf(row),
    completionOf(row),
    passRateOf(row),
    formatNumber(row.overdue),
  ]);

  autoTable(doc, {
    startY: 128,
    margin: { left: margin, right: margin },
    head: [['#', payload.levelLabel, 'Score', 'Band', `Δ ${payload.deltaPhrase}`, 'Completion', 'Pass (of completed)', 'Overdue']],
    body,
    styles: {
      font: 'helvetica',
      fontSize: 9,
      textColor: [...PDF.ink] as [number, number, number],
      fillColor: [...PDF.plate] as [number, number, number],
      lineColor: [...PDF.champagne] as [number, number, number],
      lineWidth: 0.4,
      cellPadding: 5,
    },
    headStyles: {
      fillColor: [...PDF.racing] as [number, number, number],
      textColor: [250, 247, 238] as [number, number, number],
      fontStyle: 'bold',
      fontSize: 8.5,
    },
    alternateRowStyles: { fillColor: [246, 241, 231] as [number, number, number] },
  });

  // Top performers / Needs attention — recognition with equal prominence.
  let y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 26;
  const writeRail = (title: string, color: readonly [number, number, number], nodes: PerfNode[]) => {
    if (nodes.length === 0) return;
    if (y > doc.internal.pageSize.getHeight() - 90) {
      doc.addPage();
      y = 50;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...color);
    doc.text(title, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...PDF.ink);
    nodes.forEach((n, i) => {
      doc.text(
        `${i + 1}. ${n.name} — score ${scoreOf(n)} (${bandOfNode(n)}), ${completionOf(n)} complete, ${formatNumber(n.overdue)} overdue`,
        margin + 8,
        y + 16 + i * 14
      );
    });
    y += 16 + nodes.length * 14 + 18;
  };
  writeRail('Top performers', PDF.bottle, payload.topPerformers);
  writeRail('Needs attention', PDF.oxblood, payload.needsAttention);

  doc.save(reportFilename(payload, 'pdf'));
}

export function exportReport(payload: ReportPayload, format: ExportFormat): void {
  if (format === 'csv') exportCsv(payload);
  else if (format === 'xlsx') exportXlsx(payload);
  else exportPdf(payload);
}
